import {
  Dashboard,
  DashboardConfig,
  DashboardId,
} from '../../common/components/dashboards/types';
import { ExecutorRoleViewAuthorizationService } from '../authorization/service';
import { HasDependencies, hashString } from '../types';
import {
  DashboardNotAllowedError,
  DashboardNotFoundError,
} from './dashboard-errors';
import { DashboardService } from './dashboard-service';
import { DomainViewDashboardService } from './domain-view-dashboard-service';

export class UserViewDashboardService
  implements DashboardService, HasDependencies
{
  constructor(
    private readonly domainViewDashboardService: DomainViewDashboardService,
    private readonly executorRoleViewAuthorizationService: ExecutorRoleViewAuthorizationService
  ) {}

  public getDeps = () => [
    hashString([
      ...this.domainViewDashboardService.getDeps(),
      ...this.executorRoleViewAuthorizationService.getDeps(),
    ]),
  ];

  // Returns allowed dashboards with allowed pages
  public listDashboards = async () => {
    const domainAllowedDashboards: Dashboard[] =
      await this.domainViewDashboardService.listDashboards();
    const allowedDashboards: Dashboard[] = domainAllowedDashboards.filter(
      (db) => {
        return this.executorRoleViewAuthorizationService.canSeeDashboard(db.id);
      }
    );
    const allowedDashboardsWithAllowedPages: Dashboard[] =
      allowedDashboards.map((db) => {
        const pages = db.pages.filter((p) => {
          const isDashboardPageAllowed =
            this.executorRoleViewAuthorizationService.canSeeDashboardPage(p);
          return p.enabled && isDashboardPageAllowed;
        });
        return { ...db, pages };
      });
    return allowedDashboardsWithAllowedPages;
  };

  public listCustomDashboardConfigs = async () => {
    const dashboardConfigs =
      await this.domainViewDashboardService.listCustomDashboardConfigs();
    return dashboardConfigs.filter(
      this.executorRoleViewAuthorizationService.canSeeCustomDashboard
    );
  };

  public getCustomDashboardConfig = async (
    dashboardId: DashboardId
  ): Promise<
    DashboardConfig | DashboardNotFoundError | DashboardNotAllowedError
  > => {
    const dashboardConfig =
      await this.domainViewDashboardService.getCustomDashboardConfig(
        dashboardId
      );
    if (dashboardConfig instanceof DashboardNotFoundError) {
      return dashboardConfig; // TODO: make clear that this is an error by using either
    } else {
      const isDashboardAllowed =
        this.executorRoleViewAuthorizationService.canSeeCustomDashboard(
          dashboardConfig
        );
      return isDashboardAllowed
        ? dashboardConfig
        : new DashboardNotAllowedError(dashboardId);
    }
  };
}
